/**
 * MIS AULAS (A4) — la portada del alumno: una tarjeta por cohorte en la que
 * está matriculado, con su avance y la puerta a su aula.
 *
 * Hasta ahora `/academy/cohorts` era la única pantalla de cohortes y sólo la
 * veía el escalón docente: un alumno no tenía ninguna superficie de su aula
 * aunque el dato ya estuviera en su dispositivo. Esto no abre ninguna fuente
 * nueva — lista las matrículas propias que ya sincronizan.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { GraduationCap, School } from "lucide-solid";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { HelpButton } from "@/features/help/components/help-button";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { CompletionProgress } from "../../completion/completion-progress";
import { useUserEnrollments } from "../../hooks/use-people-data";
import { useCohortChatUnread } from "../../hooks/use-classroom-chat";

/**
 * B2 · burbuja de mensajes sin leer de un aula. Se calcula sobre lo que ya está
 * sincronizado —no hay endpoint que preguntar— comparando contra MI marca de
 * leído, que sí viaja entre dispositivos.
 */
const UnreadBubble: Component<{ cohortId: string }> = (props) => {
  const unread = useCohortChatUnread(() => props.cohortId);
  return (
    <Show when={unread() > 0}>
      <span
        class="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-accent-foreground"
        data-testid="classroom-card-unread"
      >
        {unread()}
      </span>
    </Show>
  );
};

/** Matrículas que dan acceso a un aula (una baja no tiene aula que abrir). */
const OPEN_STATUSES = new Set(["active", "completed"]);

export const MyClassroomsView: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();
  const enrollments = useUserEnrollments(userId);
  const chatFlag = useFeatureEnabled("academy.classroom.chat");

  const rooms = () =>
    (enrollments.data ?? []).filter((e) => OPEN_STATUSES.has(e.status ?? "") && e.cohortId);

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5">
        <div class="flex items-start justify-between gap-2">
          <PageHeader
            title={t(msg`My classrooms`)}
            description={t(msg`Your cohorts: announcements, class plan and progress.`)}
            icon={School}
          />
          <HelpButton area="classroom" />
        </div>

        <Show when={!enrollments.isLoading} fallback={<Skeleton class="h-32 w-full rounded-2xl" />}>
          <Show
            when={rooms().length > 0}
            fallback={
              <EmptyState
                icon={GraduationCap}
                title={t(msg`You have no classroom yet`)}
                description={t(
                  msg`When the academy enrolls you in a cohort, its classroom shows up here.`,
                )}
              />
            }
          >
            <ul class="space-y-3" data-testid="my-classrooms">
              <For each={rooms()}>
                {(e) => (
                  <li>
                    <Link
                      to={`/classroom/${e.cohortId}`}
                      class="block space-y-2 rounded-2xl border border-border p-4 transition-colors hover:border-primary/50"
                      data-testid="classroom-card"
                      data-cohort-id={e.cohortId ?? ""}
                    >
                      <div class="flex flex-wrap items-center justify-between gap-2">
                        <p class="font-display text-lg font-bold">
                          {e.cohortName ?? e.cohortCode ?? e.cohortId}
                        </p>
                        <span class="flex items-center gap-2">
                          <Show when={chatFlag().enabled && e.cohortId}>
                            {(cid) => <UnreadBubble cohortId={cid()} />}
                          </Show>
                          <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            {e.status}
                          </span>
                        </span>
                      </div>
                      <Show when={e.cohortId}>
                        {(cid) => <CompletionProgress cohortId={cid()} />}
                      </Show>
                    </Link>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};
